import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Send, Bot, User, ArrowLeft, Sparkles, AlertCircle, Crown, History, MoreVertical, Pencil, Trash2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { apiClient, useAuth } from "../App";
import { toast } from "sonner";

const AI_HISTORY_INDEX_KEY = "ai_launcher_history_index_v1";

const normalizeAssistantText = (text) => {
  if (!text) return "";
  return String(text)
    .replace(/^#{1,6}\s*/gm, "")
    .replace(/\*\*(.*?)\*\*/g, "$1")
    .replace(/^\s*[-*]\s+/gm, "• ")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
};

const inferResponseStyleFromMessage = (message) => {
  const text = (message || "").toLowerCase().trim();
  if (!text) return "simple";
  const expertHints = [
    "architecture",
    "api",
    "database",
    "scal",
    "latency",
    "infra",
    "stack",
    "trade-off",
    "algorithm",
    "schema",
    "implementation",
    "auth",
  ];
  const businessHints = [
    "pricing",
    "price",
    "market",
    "go-to-market",
    "gtm",
    "revenue",
    "cac",
    "ltv",
    "segment",
    "positioning",
    "sales",
    "funnel",
    "acquisition",
    "retention",
  ];
  if (expertHints.some((hint) => text.includes(hint))) return "expert";
  if (businessHints.some((hint) => text.includes(hint))) return "business";
  if (text.length > 260) return "business";
  return "simple";
};

const getHistoryBucketLabel = (dateValue) => {
  const d = new Date(dateValue);
  if (Number.isNaN(d.getTime())) return "Older";
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const bucketDate = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  if (bucketDate.getTime() === today.getTime()) return "Today";
  if (bucketDate.getTime() === yesterday.getTime()) return "Yesterday";
  return "Older";
};

const AIBuilderPage = () => {
  const { opportunityId } = useParams();
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get("projectId");
  const navigate = useNavigate();
  const { user } = useAuth();
  const debugPlanEnabled = localStorage.getItem("plan_override_enabled") === "true";
  const overridePlan = (localStorage.getItem("test_plan_override") || "").trim().toLowerCase();
  const hasAdminPin = Boolean((localStorage.getItem("admin_plan_pin") || "").trim());
  const canUsePlanOverride = debugPlanEnabled && hasAdminPin;
  const effectivePlan =
    canUsePlanOverride && ["free", "pro", "full_access"].includes(overridePlan)
      ? overridePlan
      : (user?.plan || "free").toLowerCase();
  const launcherTestMode = false;
  const messagesEndRef = useRef(null);
  const chatScrollRef = useRef(null);
  const shouldAutoScrollRef = useRef(true);
  const selectedOpportunity = opportunityId || "";
  const [opportunityDetails, setOpportunityDetails] = useState(null);
  const [projectWorkspace, setProjectWorkspace] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [historyItems, setHistoryItems] = useState([]);
  const [activeHistoryId, setActiveHistoryId] = useState(null);
  const [showMobileHistory, setShowMobileHistory] = useState(false);
  const [responseStyle, setResponseStyle] = useState("auto");

  const localHistoryKey = projectId ? `ai_launcher_history_${projectId}` : null;

  const loadHistoryIndex = useCallback(() => {
    try {
      const raw = localStorage.getItem(AI_HISTORY_INDEX_KEY);
      const all = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(all)) return [];
      return all;
    } catch (_error) {
      return [];
    }
  }, []);

  const persistHistoryIndex = useCallback((items) => {
    localStorage.setItem(AI_HISTORY_INDEX_KEY, JSON.stringify(items));
  }, []);

  const upsertHistory = useCallback(
    ({ nextMessages, nextSessionId, forcedId = null }) => {
      const all = loadHistoryIndex();
      const id = forcedId || activeHistoryId || `conv_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
      const firstUser = nextMessages.find((m) => m.role === "user")?.content || "";
      const title = firstUser.slice(0, 72) || projectWorkspace?.name || opportunityDetails?.name || "New conversation";
      const updated = {
        id,
        projectId: projectId || null,
        opportunityId: selectedOpportunity || null,
        title,
        sessionId: nextSessionId || null,
        updatedAt: new Date().toISOString(),
        messages: nextMessages,
      };
      const merged = [updated, ...all.filter((item) => item.id !== id)].sort(
        (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
      );
      persistHistoryIndex(merged);
      const filtered = merged.filter(
        (item) =>
          (projectId ? item.projectId === projectId : item.opportunityId === selectedOpportunity)
      );
      setHistoryItems(filtered);
      setActiveHistoryId(id);
      return id;
    },
    [
      activeHistoryId,
      loadHistoryIndex,
      opportunityDetails?.name,
      persistHistoryIndex,
      projectId,
      projectWorkspace?.name,
      selectedOpportunity,
    ]
  );

  useEffect(() => {
    if (selectedOpportunity) {
      fetchOpportunityDetails(selectedOpportunity);
      if (!projectId) {
        const all = loadHistoryIndex();
        const filtered = all.filter((item) => item.opportunityId === selectedOpportunity && !item.projectId);
        setHistoryItems(filtered);
        if (filtered.length > 0) {
          setActiveHistoryId(filtered[0].id);
          setMessages(filtered[0].messages || []);
          setSessionId(filtered[0].sessionId || null);
        } else {
          setMessages([]);
          setSessionId(null);
          setActiveHistoryId(null);
        }
      }
    }
  }, [selectedOpportunity, projectId, loadHistoryIndex]);

  useEffect(() => {
    if (!shouldAutoScrollRef.current) return;
    scrollToBottom("auto");
  }, [messages]);

  const scrollToBottom = (behavior = "auto") => {
    const container = chatScrollRef.current;
    if (container) {
      container.scrollTo({ top: container.scrollHeight, behavior });
      return;
    }
    messagesEndRef.current?.scrollIntoView({ behavior });
  };

  const handleChatScroll = () => {
    const container = chatScrollRef.current;
    if (!container) return;
    const distanceToBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight;
    shouldAutoScrollRef.current = distanceToBottom < 120;
  };

  const loadLocalProjectChatHistory = useCallback(() => {
    if (!localHistoryKey) {
      return false;
    }

    try {
      const raw = localStorage.getItem(localHistoryKey);
      if (!raw) {
        return false;
      }

      const parsed = JSON.parse(raw);
      const restoredMessages = Array.isArray(parsed?.messages) ? parsed.messages : [];
      const restoredSessionId = parsed?.sessionId || null;

      setMessages(restoredMessages);
      setSessionId(restoredSessionId);
      return restoredMessages.length > 0;
    } catch (error) {
      console.error("Error loading local project chat history:", error);
      return false;
    }
  }, [localHistoryKey]);

  const saveLocalProjectChatHistory = useCallback((nextMessages, nextSessionId) => {
    if (!localHistoryKey) {
      return;
    }

    try {
      localStorage.setItem(
        localHistoryKey,
        JSON.stringify({
          messages: nextMessages,
          sessionId: nextSessionId || null,
        })
      );
    } catch (error) {
      console.error("Error saving local project chat history:", error);
    }
  }, [localHistoryKey]);


  const fetchOpportunityDetails = async (oppId) => {
    try {
      const response = await apiClient.get(`/opportunities/${oppId}`);
      setOpportunityDetails(response.data);
    } catch (error) {
      console.error("Error fetching opportunity details:", error);
    }
  };

  const fetchProjectWorkspace = useCallback(async () => {
    if (!projectId) {
      setProjectWorkspace(null);
      return;
    }

    try {
      const response = await apiClient.get(`/projects/${projectId}`);
      setProjectWorkspace(response.data);
    } catch (error) {
      console.error("Error fetching project workspace:", error);
      setProjectWorkspace(null);
    }
  }, [projectId]);


  const saveProjectChatMessage = async ({ content, session_id, nextMessages }) => {
    if (!projectId || !content?.trim()) {
      return;
    }

    saveLocalProjectChatHistory(nextMessages || messages, session_id || sessionId);
    upsertHistory({
      nextMessages: nextMessages || messages,
      nextSessionId: session_id || sessionId,
    });
  };

  const fetchProjectChatHistory = useCallback(async () => {
    if (!projectId) {
      return;
    }


    const restoredFromLocal = loadLocalProjectChatHistory();
    if (restoredFromLocal) {
      return;
    }

    try {
      const response = await apiClient.get(`/projects/${projectId}/chat-history`);
      const restoredMessages = response.data?.messages || [];
      const restoredSession = response.data?.session_id || null;
      setMessages(restoredMessages);
      setSessionId(restoredSession);
      saveLocalProjectChatHistory(restoredMessages, restoredSession);
      upsertHistory({
        nextMessages: restoredMessages,
        nextSessionId: restoredSession,
      });
    } catch (error) {
      console.error("Error fetching project chat history:", error);
      const restoredFromLocal = loadLocalProjectChatHistory();
      if (!restoredFromLocal) {
        setMessages([]);
        setSessionId(null);
      }
    }
  }, [projectId, loadLocalProjectChatHistory, saveLocalProjectChatHistory, upsertHistory]);

  useEffect(() => {
    fetchProjectWorkspace();
  }, [fetchProjectWorkspace]);

  useEffect(() => {
    if (projectId) {
      fetchProjectChatHistory();
    }
  }, [projectId, user?.id, fetchProjectChatHistory]);

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || !selectedOpportunity) return;

    const userMessage = inputMessage.trim();
    setInputMessage("");
    const resolvedStyle =
      responseStyle === "auto"
        ? inferResponseStyleFromMessage(userMessage)
        : responseStyle;
    const nextUserMessages = [...messages, { role: "user", content: userMessage }];
    setMessages(nextUserMessages);
    upsertHistory({
      nextMessages: nextUserMessages,
      nextSessionId: sessionId,
    });
    await saveProjectChatMessage({
      role: "user",
      content: userMessage,
      session_id: sessionId,
      nextMessages: nextUserMessages,
    });
    setLoading(true);

    try {
      const response = await apiClient.post("/ai-builder", {
        opportunity_id: selectedOpportunity,
        project_id: projectId,
        message: userMessage,
        session_id: sessionId,
        response_style: resolvedStyle,
      });

      const updatedSessionId = response.data.session_id;
      const normalizedAssistant = normalizeAssistantText(response.data.response);
      const nextAssistantMessages = [
        ...nextUserMessages,
        { role: "assistant", content: "" }
      ];

      setSessionId(updatedSessionId);
      setMessages(nextAssistantMessages);
      const total = normalizedAssistant.length;
      let current = "";
      let cursor = 0;
      while (cursor < total) {
        const chunkSize = normalizedAssistant[cursor] === "\n" ? 1 : 2;
        current += normalizedAssistant.slice(cursor, cursor + chunkSize);
        cursor += chunkSize;
        setMessages((prev) => {
          const copy = [...prev];
          if (copy.length > 0) {
            copy[copy.length - 1] = { ...copy[copy.length - 1], content: current };
          }
          return copy;
        });
        // Letter-by-letter feel without making long responses too slow.
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => setTimeout(resolve, 9));
      }
      const finalizedMessages = [
        ...nextUserMessages,
        { role: "assistant", content: normalizedAssistant }
      ];
      setMessages(finalizedMessages);
      upsertHistory({
        nextMessages: finalizedMessages,
        nextSessionId: updatedSessionId,
      });
      await saveProjectChatMessage({
        role: "assistant",
        content: normalizedAssistant,
        session_id: updatedSessionId,
        nextMessages: finalizedMessages,
      });
    } catch (error) {
      const message =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        error.message ||
        "Failed to get AI response";
      if (error.response?.status === 429) {
        toast.error(
          error.response?.data?.detail ||
            "Monthly Cofounder limit reached. Upgrade to Pro for higher limits."
        );
      } else {
        console.error("Cofounder error:", error.response?.data || error);
        toast.error(message);
      }
      const nextErrorMessages = [
        ...nextUserMessages,
        {
          role: "assistant",
          content: `Error: ${message}`,
          error: true
        }
      ];
      setMessages(nextErrorMessages);
      saveLocalProjectChatHistory(nextErrorMessages, sessionId);
      upsertHistory({
        nextMessages: nextErrorMessages,
        nextSessionId: sessionId,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSelectHistory = (item) => {
    setActiveHistoryId(item.id);
    setMessages(item.messages || []);
    setSessionId(item.sessionId || null);
    setShowMobileHistory(false);
  };

  const handleRenameHistory = (item) => {
    const nextTitle = window.prompt("Rename conversation", item.title || "")?.trim();
    if (!nextTitle || nextTitle === item.title) return;
    const all = loadHistoryIndex();
    const merged = all.map((entry) => (entry.id === item.id ? { ...entry, title: nextTitle } : entry));
    persistHistoryIndex(merged);
    const filtered = merged.filter(
      (entry) => (projectId ? entry.projectId === projectId : entry.opportunityId === selectedOpportunity)
    );
    setHistoryItems(filtered);
  };

  const handleDeleteHistory = (item) => {
    const ok = window.confirm("Delete this conversation?");
    if (!ok) return;
    const all = loadHistoryIndex();
    const merged = all.filter((entry) => entry.id !== item.id);
    persistHistoryIndex(merged);
    const filtered = merged.filter(
      (entry) => (projectId ? entry.projectId === projectId : entry.opportunityId === selectedOpportunity)
    );
    setHistoryItems(filtered);
    if (activeHistoryId === item.id) {
      setActiveHistoryId(null);
      setMessages([]);
      setSessionId(null);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const quickPrompts = [
    "Give me a simple launch plan",
    "What should I build first?",
    "How can I price this idea?",
    "How do I find my first 10 users?",
    "Explain this project in simple words"
  ];

  const groupedHistory = historyItems.reduce(
    (acc, item) => {
      const bucket = getHistoryBucketLabel(item.updatedAt);
      acc[bucket].push(item);
      return acc;
    },
    { Today: [], Yesterday: [], Older: [] }
  );

  if (!launcherTestMode && effectivePlan !== "pro" && effectivePlan !== "full_access") {
    return (
      <div className="min-h-screen bg-[#111111] flex items-center justify-center px-4">
        <div className="w-full max-w-xl rounded-2xl border border-[#2A2A2A] bg-[#111111] p-6 md:p-8">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-[#7C3AED]/35 bg-[#7C3AED]/10 px-3 py-1 text-xs font-semibold text-[#C4B5FD]">
            <Crown className="w-3.5 h-3.5" />
            Pro feature
          </div>
          <h1 className="text-2xl font-semibold text-white mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Cofounder is available on Pro
          </h1>
          <p className="text-zinc-400 mb-6">
            Upgrade your plan to access guided planning, pricing support, and execution steps with Cofounder.
          </p>
          <div className="flex gap-3">
            <Button
              variant="outline"
              className="border-[#2A2A2A] bg-white/[0.03] text-zinc-200 hover:bg-white/[0.07]"
              onClick={() => navigate("/dashboard/opportunities")}
            >
              Back
            </Button>
            <Button
              className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white"
              onClick={() => navigate("/dashboard/settings/billing")}
              data-testid="cofounder-upgrade-btn"
            >
              <Crown className="w-4 h-4 mr-2" />
              Upgrade to Pro
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col ai-builder-shell antialiased">
      {/* Header */}
      <div className="p-4 md:p-6 border-b border-[#1F1F1F] bg-[#111111]/80 backdrop-blur-xl">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              className="text-zinc-400 hover:text-white"
              onClick={() => navigate("/dashboard/opportunities")}
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl ai-neon-chip flex items-center justify-center">
                <Bot className="w-5 h-5 text-[#7C3AED]" />
              </div>
              <div>
                <h1 className="text-lg font-semibold text-white inline-flex items-center gap-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                  Cofounder
                  {effectivePlan === "free" ? (
                    <Crown className="w-4 h-4 text-[#A78BFA]" />
                  ) : null}
                </h1>
                <p className="text-xs text-zinc-500">Turn your idea into a clear action plan</p>
              </div>
            </div>
          </div>
          <Button
            variant="outline"
            className="lg:hidden w-fit bg-white/[0.02] border-[#2A2A2A] text-zinc-300 hover:text-white hover:bg-white/[0.06]"
            onClick={() => setShowMobileHistory((prev) => !prev)}
          >
            <History className="w-4 h-4 mr-2" />
            History
          </Button>
          <div className="w-[180px]">
            <Select value={responseStyle} onValueChange={setResponseStyle}>
              <SelectTrigger className="h-9 bg-[#161616]/95 border-[#2A2A2A] text-zinc-200">
                <SelectValue placeholder="Response mode" />
              </SelectTrigger>
              <SelectContent className="bg-[#111111] border-[#1F1F1F] text-zinc-200">
                <SelectItem value="auto">Auto</SelectItem>
                <SelectItem value="simple">Simple</SelectItem>
                <SelectItem value="business">Business</SelectItem>
                <SelectItem value="expert">Expert</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1 md:max-w-md ml-auto">
            {(opportunityDetails || projectWorkspace) && (
              <div className="ai-panel rounded-xl px-4 py-3">
                <p className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 mb-1">
                  {projectWorkspace ? "Current Project" : "Current Opportunity"}
                </p>
                <p className="text-sm text-zinc-200 truncate font-medium">
                  {projectWorkspace?.name || opportunityDetails?.name}
                </p>
                {projectId && (
                  <>
                    <p className="text-xs text-zinc-500 mt-1 truncate">
                      Project ID: {projectId}
                    </p>
                    <p className="text-xs text-emerald-400 mt-1 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      Chat is synced for this project.
                    </p>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Plan Limit Warning */}
        {!launcherTestMode && effectivePlan !== "pro" && effectivePlan !== "full_access" && (
          <div className="mt-4 flex items-center gap-2 px-4 py-2 rounded-lg bg-[#7C3AED]/10 border border-[#7C3AED]/20 text-sm">
            <Crown className="w-4 h-4 text-[#A78BFA]" />
            <span className="text-zinc-300">
              Cofounder requires Pro. Upgrade to unlock guided planning.
            </span>
            <Button
              variant="link"
              className="text-[#A78BFA] hover:text-[#C4B5FD] p-0 h-auto ml-auto"
              onClick={() => navigate("/dashboard/settings/billing")}
              data-testid="upgrade-link"
            >
              Upgrade →
            </Button>
          </div>
        )}
      </div>

      {/* Chat Area */}
      <div className="flex-1 min-h-0 flex">
        <aside className="hidden lg:flex w-72 border-r border-[#1F1F1F] bg-[#0F0F0F]/80 backdrop-blur-xl flex-col">
          <div className="p-3 overflow-y-auto space-y-4">
            {historyItems.length === 0 ? (
              <p className="text-xs text-zinc-500 px-2 py-3">No conversation yet.</p>
            ) : (
              ["Today", "Yesterday", "Older"].map((bucket) =>
                groupedHistory[bucket].length ? (
                  <div key={bucket} className="space-y-2">
                    <p className="text-[11px] uppercase tracking-wider text-zinc-500 px-1">{bucket}</p>
                    {groupedHistory[bucket].map((item) => (
                      <div
                        key={item.id}
                        className={`w-full text-left px-3 py-2 rounded-lg border transition-all ${
                          activeHistoryId === item.id
                            ? "bg-[#7C3AED]/10 border-[#7C3AED]/30 text-white"
                            : "bg-white/[0.02] border-[#1F1F1F] text-zinc-400 hover:text-white hover:border-[#2A2A2A]"
                        }`}
                      >
                        <button className="w-full text-left" onClick={() => handleSelectHistory(item)}>
                          <p className="text-sm font-medium line-clamp-2">{item.title}</p>
                          <p className="text-[11px] text-zinc-500 mt-1">
                            {new Date(item.updatedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                          </p>
                        </button>
                        <div className="mt-2 flex justify-end">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button className="h-7 w-7 rounded-md border border-[#2A2A2A] bg-white/[0.02] text-zinc-400 hover:text-white inline-flex items-center justify-center">
                                <MoreVertical className="w-3.5 h-3.5" />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="bg-[#111111] border-[#1F1F1F]">
                              <DropdownMenuItem
                                className="text-zinc-300 hover:text-white cursor-pointer"
                                onClick={() => handleRenameHistory(item)}
                              >
                                <Pencil className="w-4 h-4 mr-2" />
                                Rename
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="text-red-400 hover:text-red-300 cursor-pointer"
                                onClick={() => handleDeleteHistory(item)}
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null
              )
            )}
          </div>
        </aside>
        <div
          ref={chatScrollRef}
          onScroll={handleChatScroll}
          className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4 ai-chat-surface"
        >
        {showMobileHistory && (
          <div className="lg:hidden mb-4 rounded-xl border border-[#2A2A2A] bg-[#101018]/95 backdrop-blur-xl p-3 space-y-2">
            {historyItems.length === 0 ? (
              <p className="text-xs text-zinc-500 px-2 py-2">No conversation yet.</p>
            ) : (
              historyItems.slice(0, 8).map((item) => (
                <div key={item.id} className="w-full px-3 py-2 rounded-lg border bg-white/[0.02] border-[#1F1F1F]">
                  <button
                    onClick={() => handleSelectHistory(item)}
                    className={`w-full text-left ${activeHistoryId === item.id ? "text-white" : "text-zinc-300"}`}
                  >
                    <p className="text-sm font-medium line-clamp-2">{item.title}</p>
                  </button>
                  <div className="mt-2 flex justify-end">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button className="h-7 w-7 rounded-md border border-[#2A2A2A] bg-white/[0.02] text-zinc-400 hover:text-white inline-flex items-center justify-center">
                          <MoreVertical className="w-3.5 h-3.5" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-[#111111] border-[#1F1F1F]">
                        <DropdownMenuItem
                          className="text-zinc-300 hover:text-white cursor-pointer"
                          onClick={() => handleRenameHistory(item)}
                        >
                          <Pencil className="w-4 h-4 mr-2" />
                          Rename
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="text-red-400 hover:text-red-300 cursor-pointer"
                          onClick={() => handleDeleteHistory(item)}
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
        {!selectedOpportunity ? (
          <div className="h-full flex items-center justify-center">
            <div className="text-center max-w-md">
              <Sparkles className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-white mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                Start from an opportunity
              </h2>
              <p className="text-zinc-500">
                Open this from Opportunities or My Projects to start your guided conversation.
              </p>
            </div>
          </div>
        ) : messages.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center">
            {opportunityDetails && (
              <div className="text-center max-w-lg mb-8">
                <div className="w-16 h-16 rounded-2xl ai-neon-chip flex items-center justify-center mx-auto mb-4">
                  <Bot className="w-8 h-8 text-[#7C3AED]" />
                </div>
                <h2 className="text-2xl font-semibold text-white mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                  Let’s build {projectWorkspace?.name || opportunityDetails.name}
                </h2>
                <p className="text-zinc-400 mb-3">
                  I can help with what to build first, how to price it, and how to get your first users.
                </p>
                {projectId && (
                  <p className="text-xs text-zinc-500 mb-8">
                    This chat is linked to your project, so you can continue anytime.
                  </p>
                )}
              </div>
            )}

            {/* Quick Prompts */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-2xl w-full">
              {quickPrompts.map((prompt, i) => (
                <button
                  key={i}
                  className="p-4 rounded-xl bg-[#111111]/90 border border-[#1F1F1F] hover:border-[#3B2A66] text-left transition-all text-sm text-zinc-300 hover:text-white hover:shadow-[0_0_24px_rgba(124,58,237,0.15)]"
                  onClick={() => {
                    setInputMessage(prompt);
                  }}
                  data-testid={`quick-prompt-${i}`}
                >
                  <Sparkles className="w-4 h-4 text-[#7C3AED] mb-2" />
                  {prompt}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <>
            {messages.map((msg, i) => (
              <div
                key={i}
                className={`flex gap-4 ${msg.role === "user" ? "justify-end" : "justify-start"}`}
              >
                {msg.role === "assistant" && (
                  <div className="w-8 h-8 rounded-lg bg-[#7C3AED]/10 flex items-center justify-center flex-shrink-0 mt-1">
                    <Bot className="w-4 h-4 text-[#7C3AED]" />
                  </div>
                )}
                <div
                  className={`max-w-[80%] md:max-w-[70%] rounded-2xl p-4 ${
                    msg.role === "user"
                      ? "bg-[#7C3AED]/12 border border-[#7C3AED]/30 shadow-[0_0_22px_rgba(124,58,237,0.18)]"
                      : msg.error
                      ? "bg-red-500/10 border border-red-500/20"
                      : "glass ai-assistant-bubble"
                  }`}
                  data-testid={`message-${i}`}
                >
                  <p className="text-zinc-200 whitespace-pre-wrap leading-relaxed">
                    {msg.content}
                  </p>
                </div>
                {msg.role === "user" && (
                  <div className="w-8 h-8 rounded-lg bg-white/[0.03] flex items-center justify-center flex-shrink-0 mt-1">
                    <User className="w-4 h-4 text-zinc-400" />
                  </div>
                )}
              </div>
            ))}

            {loading && (
              <div className="flex gap-4">
                <div className="w-8 h-8 rounded-lg bg-[#7C3AED]/10 flex items-center justify-center flex-shrink-0 mt-1">
                  <Bot className="w-4 h-4 text-[#7C3AED]" />
                </div>
                <div className="glass ai-assistant-bubble rounded-2xl p-4">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 rounded-full bg-[#7C3AED] animate-bounce" style={{ animationDelay: "0ms" }}></div>
                    <div className="w-2 h-2 rounded-full bg-[#7C3AED] animate-bounce" style={{ animationDelay: "150ms" }}></div>
                    <div className="w-2 h-2 rounded-full bg-[#7C3AED] animate-bounce" style={{ animationDelay: "300ms" }}></div>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </>
        )}
        </div>
      </div>

      {/* Input Area */}
      {selectedOpportunity && (
        <div className="p-4 md:p-6 border-t border-[#1F1F1F] bg-[#111111]/80 backdrop-blur-xl">
          <div className="max-w-4xl mx-auto flex gap-3 items-end">
            <Textarea
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder="Ask anything about your idea. I will answer in simple steps."
              className="flex-1 min-h-[52px] max-h-32 resize-none bg-[#161616]/95 border-[#2A2A2A] rounded-xl px-4 py-3 text-white placeholder:text-zinc-500 focus:border-[#7C3AED] focus:ring-[#7C3AED]/50"
              disabled={loading}
              data-testid="chat-input"
            />
            <Button
              className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white rounded-xl px-6 h-[52px] shadow-[0_0_28px_rgba(124,58,237,0.28)]"
              onClick={handleSendMessage}
              disabled={loading || !inputMessage.trim()}
              data-testid="send-btn"
            >
              <Send className="w-5 h-5" />
            </Button>
          </div>
          <p className="text-xs text-zinc-600 text-center mt-3">
            Mode: {responseStyle === "auto" ? `Auto (${inferResponseStyleFromMessage(inputMessage || messages.slice(-1)[0]?.content || "")})` : responseStyle}. AI guidance helps you decide faster. Always validate with real users before launch.
          </p>
        </div>
      )}
    </div>
  );
};

export default AIBuilderPage;
